<template>
    <div class="checkin-dates">
        <slot name="before"/>

        <div class="row">
            <small v-if="!singleDateSelection" class="col-12 mb-2 text-secondary">
                {{ $t('bookingService.form.multidate_selection.prefix') }}
            </small>

            <template v-for="(entry, index) in checkinDatesComputed">
                <datepicker
                    :class="calendarClass"
                    :key="'datepicker' + index"
                    :value="entry"
                    @selected="updateDate($event, index)"
                    :placeholder="getPlaceholder(index)"
                    input-class="date-input"
                    :disabledDates="disabledDates"
                    :format="formatDate"
                    :name="'date_' + index"
                    :inline="singleDateSelection"
                    :language="calendarLocale"
                    :highlighted="highlightedDates"
                    :required="index === 0"
                    :typeable="false"
                    bootstrap-styling
                    monday-first
                    :open-date="firstDate"
                    :ref="'datepicker' + index"
                >
                    <template v-if="!singleDateSelection" v-slot:afterDateInput>
                        <span class="checkin-dates__suffix">{{ appendDuration(index) }}</span>
                        <ClearIcon v-if="entry" class="checkin-dates__icon checkin-dates__clear-icon" @click="clearDate(index)" />
                        <CalendarIcon class="checkin-dates__icon checkin-dates__calendar-icon" @click="openCalendar"/>
                    </template>
                </datepicker>

                <div v-if="!singleDateSelection && alternativeDatesAreEmpty && !index" class="col-12 mb-2">
                    <small class="checkin-dates__alternative-hint">
                        {{$t('bookingService.multiDate.alternativeHint')}}
                    </small>
                </div>

                <div class="w-100"></div>
            </template>
        </div>

        <!--<input type="text" v-if="singleDateSelection" name="singleDate" required v-model="checkinDates[0]" class="d-none"/>-->
    </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker'
import { en, de, cs, nl } from 'vuejs-datepicker/dist/locale'
import CalendarIcon from '../../../animod-shop-ssr/src/assets/icons/calendar.svg';
import ClearIcon from '../../../animod-shop-ssr/src/assets/icons/close.svg';
import { mapState } from 'vuex';

export default {
    name: 'CheckinDates',
    components: {
        Datepicker,
        CalendarIcon,
        ClearIcon
    },
    data () {
        return {
            en, de
        }
    },
    props: {
        checkinDates: {
            type: Array,
            required: true
        },
        duration: {
            type: Number,
            required: true
        },
        singleDateSelection: Boolean
    },
    computed: {
        ...mapState(['activationData', 'locale']),
        calendarClass() {
            return this.singleDateSelection ? 'col-12 col-md-8 col-xl-6 mx-auto fullwidth-calendar' : 'col-12 col-md-6 multi-calendar';
        },
        checkinDatesComputed () {
            return Array.from({
                    length: this.singleDateSelection ? 1 : 3
                },
                (item, index) => this.parse(this.checkinDates[index]) || "");
        },

        calendarLocale() {
            if (this.locale.includes('de')) {
                return de;
            }
            else if (this.locale.includes('en')) {
                return en;
            }
            else if (this.locale.includes('cs')) {
                return cs;
            }
            else if (this.locale.includes('nl')) {
                return nl;
            }
            return de;
        },

        highlightedDates() {
            if (!this.activationData.availableDates.length) {
                //calculating the inverse of blocked dates and validUntil
                const self = this;
                return {
                    to: self.parse(this.activationData.arrangement.validUntil),
                    customPredictor (date) {
                        const dateString = self.formatDate(date);
                        const untilDate = self.parse(self.activationData.arrangement.validUntil);
                        return date < untilDate
                            && !self.checkinDates.includes(dateString);
                    }
                }
            }
            return {
                dates: this.activationData.availableDates.map(dateString => this.parse(dateString))
            };
        },
        disabledDates() {
            const datesCombined = new Set([
                ...this.activationData.blockedDates,
                ...this.checkinDates.filter(date => !!date)
            ]);
            const whitelistedDates = this.activationData.availableDates.length ? this.activationData.availableDates : [];
            return {
                to: this.firstDate || new Date(),
                dates: [...datesCombined].map(dateString => this.parse(dateString)),
                customPredictor: function(date) {
                    return whitelistedDates.length && !whitelistedDates.includes(this.formatDate(date));
                }.bind(this)
            };
        },
        firstDate() {
            return this.highlightedDates.dates ?
                [...this.highlightedDates.dates].sort((d1, d2) => {
                    if (d1 > d2) return 1;
                    else return -1;
                    //return d1 < d2
                })[0] : undefined;
        },

        alternativeDatesAreEmpty() {
            return this.checkinDatesComputed.every((d, index) => {
                if (index > 0) {
                    return !d;
                }
                else {
                    return true;
                }
            })
        }
    },
    methods: {
        parse(dateString) {
            if (!dateString || dateString.length !== 10) {
                return "";
            }

            const isGermanDateString = ~dateString.indexOf('.');
            const isAmericanDateString = ~dateString.indexOf('/')
            if (isGermanDateString) {
                const [day, month, year] = dateString.split('.');
                return new Date(+year, +month - 1, +day);
            }
            else if (isAmericanDateString) {
                return new Date(dateString);
            }
        },
        /**
         * Converts Date instance to locale string
         * @param {Date} date
         * @returns {String} dateString
         */
        formatDate(date) {
            return new Intl.DateTimeFormat("de", {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(date);
        },
        appendDuration(index) {
            const date = this.checkinDatesComputed[index];
            if (date) {
                const dateCopy = new Date(date.getTime());
                dateCopy.setDate(dateCopy.getDate() + this.duration);
                const formatted = this.formatDate(dateCopy);
                return `(${this.$t('bookingService.form.multidate_selection.suffix')} ${formatted})`;
            }
            return '';
        },
        getPlaceholder (index) {
            return `${index + 1}. ${this.$t('bookingService.form.multidate_selection.placeholder')}${!index ? '*' : ''}`;
        },
        updateDate(value, index) {
            const dates = [...this.checkinDates];
            dates[index] = !value ? '' : this.formatDate(value);
            this.$emit('update:checkinDates', dates);
        },
        openCalendar(e) {
            e.target.previousElementSibling && e.target.previousElementSibling.click();
        },
        clearDate(index) {
            this.updateDate(null, index);
        }
    }
}
</script>

<style lang="scss">
@use "@/styles/_variables";
@use "sass:map";

.vdp-datepicker {
    margin-bottom: 0.5rem;

    &.fullwidth-calendar {
        width: 100%;

        .cell {
            height: 2.75rem;
            line-height: 2.75rem;

            @media (max-width: 575px) {
                height: 2.813rem;
                line-height: 2.813rem;
            }
            &.highlighted {
                background: rgba(141, 228, 56, 0.3);
            }

            &.selected {
                background: map.get(variables.$colors, 'primary');;
                color: #fff;
            }

            &:not(.disabled).day:hover {
                border: 1px solid #81d135;
            }
        }
    }


    &.multi-calendar {

        .cell {

            &.highlighted {
                background: rgba(56, 168, 228, 0.3);
            }

            &.selected {
                background: map.get(variables.$colors, 'primary');;
                color: #fff;
            }

            &:not(.disabled).day:hover {
                border: 1px solid #358bd1;
            }
        }
    }

    .vdp-datepicker__calendar {

        .cell {
            height: 2.813rem;
            line-height: 2.813rem;
            font-weight: bold;
        }
    }
}
</style>

<style scoped lang="scss">
@use "@/styles/_variables";
@use "sass:map";

    ::v-deep .input-group .date-input {
        border-radius: 4px;

        &[readonly] {
            background-color: hsl(0, 0%, 100%);
        }
    }

    .checkin-dates {
        &__alternative-hint {
            display: block;
            color: map.get(variables.$colors, 'primary');
        }

        $muted-color: lighten(map-get(variables.$colors, 'secondary'), 20%);

        &__icon {
            width: 20px;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            color: $muted-color;
            fill: currentColor;
            z-index: 10;
        }

        &__suffix {
            position: absolute;
            left: 8em;
            font-size: 80%;
            top: 12px;
            color: $muted-color;
            z-index: 10;
        }

        &__calendar-icon {
            right: 10px;
        }

        &__clear-icon {
            right: 35px;
            cursor: pointer;
        }
    }
</style>
